import {
  APIProvider,
  Map as GoogleMap,
  useMapsLibrary,
  useMap,
  useApiIsLoaded,
} from "@vis.gl/react-google-maps";
import { useEffect, useState, FunctionComponent } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import Person from "@mui/icons-material/Person";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { MapMarkerInput, WebMarkerInput, MapPoint, MapWrapperProps, TrackingMarkerInput } from "./types";
import { escapeHtml, getPointsOnly, hashCode } from "./helpers";
import { createIconLookupTable } from "./icon-renderer";
import {
  generateMultiVisitPatientMarker,
  generateMultiVisitProviderMarker,
  generateSingleProviderMarker,
  generateSinglePatientMarker,
  applySinglePatientMarkerStyling,
  getMultiPatientMarkerClass,
  getMultiProviderMarkerClass,
  getSingleProviderMarkerClass
} from "./marker-html-generators";
import "./marker.css";

const verbose = false;

function showCustomTooltip(event: MouseEvent, text: string) {
  let tooltip = document.getElementById("custom-map-tooltip");
  if (!tooltip) {
    tooltip = document.createElement("div");
    tooltip.id = "custom-map-tooltip";
    document.body.appendChild(tooltip);
  }
  // Escape untrusted text (patient/provider names, visit labels) before
  // building HTML, then support multi-line tooltips via literal <br/> tags.
  tooltip.innerHTML = escapeHtml(text).replace(/\n/g, "<br/>");
  tooltip.style.left = event.pageX + 10 + "px";
  tooltip.style.top = event.pageY + 10 + "px";
  tooltip.style.display = "block";
}

function hideCustomTooltip() {
  const tooltip = document.getElementById("custom-map-tooltip");
  if (tooltip) {
    tooltip.style.display = "none";
  }
}

function calculateAndDisplayRoute(
  directionsService: google.maps.DirectionsService,
  directionsRenderer: google.maps.DirectionsRenderer,
  points: google.maps.LatLngLiteral[]
) {
  if (points.length < 2) return;

  if (points.length > 24) {
    console.error(
      `Trunctated ${points.length} points to 25 for driving directions`
    );
    points = points.slice(0, 24);
  }

  // Loop back to start at end of shift
  const home = points.shift() as google.maps.LatLngLiteral;

  directionsService
    .route({
      origin: home,
      destination: home,
      travelMode: google.maps.TravelMode.DRIVING,
      waypoints: points.map((point) => {
        return { location: point, stopover: true };
      }),
    })
    .then((response) => {
      directionsRenderer.setOptions({suppressMarkers: true, preserveViewport: true})
      directionsRenderer.setDirections(response);
    })
    .catch((e) => alert("Directions request failed due to " + e.toString()));
}

const TrackingComponent = (
  map: google.maps.Map,
  trackingPoints: MapPoint[],
  trackingMarker?: TrackingMarkerInput
) => {
  if (trackingPoints.length == 0) return;

  //TODO color by time (two lines?)
  const providerPath = new google.maps.Polyline({
    path: trackingPoints,
    geodesic: false,
    strokeColor: 'rgba(0, 212, 21, 0.8)',
    strokeOpacity: 1.0,
    strokeWeight: 4,
  });
  
  providerPath.setMap(map);

  const markerVariant = trackingMarker?.variant ?? "active";
  if (markerVariant === "hidden") return;

  const lastPoint = trackingPoints[trackingPoints.length - 1];

  const lastPointMarkerElement = document.createElement("div");
  lastPointMarkerElement.className = "last-location-marker";

  const lastPointBubbleElement = document.createElement("div");
  const useSosMarker = trackingMarker?.icon === "person";
  lastPointBubbleElement.className = useSosMarker ? "last-location-person-bubble" : "last-location-bubble";
  if (useSosMarker) {
    lastPointBubbleElement.innerHTML = renderToStaticMarkup(
      <Person style={{ color: "#ffffff", fontSize: 20 }} />
    );
  }
  lastPointMarkerElement.appendChild(lastPointBubbleElement);

  if (markerVariant === "sleeping") {
    const sleepingElement = document.createElement("div");
    sleepingElement.className = "last-location-sleep";
    sleepingElement.innerText = "Zzz..";
    lastPointMarkerElement.appendChild(sleepingElement);
  }

  new google.maps.marker.AdvancedMarkerElement({
    map: map,
    position: lastPoint,
    content: lastPointMarkerElement,
    title: trackingMarker?.timestamp?.toLocaleString() || "Last location"
  });
};

const MarkerComponent = (
  map: google.maps.Map,
  mapMarkerInputs: MapMarkerInput[],
  displayRoute: boolean,
  clusterMarkers: boolean,
  onPatientClick: MapWrapperProps["onPatientClick"]
) => {
  if (!mapMarkerInputs) return;
  if (verbose) console.log("Add markers", mapMarkerInputs);

  const iconLookup = createIconLookupTable(
    mapMarkerInputs.filter((input): input is WebMarkerInput => 'icon' in input).map((input) => input.icon)
  );

  const allMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
  const BASE_Z_INDEX = 100;
  const HOVER_Z_INDEX = 1000;

  const markers = mapMarkerInputs.filter((input): input is WebMarkerInput => 'icon' in input).map((input, index) => {

    const providerMarker = document.createElement("div");

    // Patient Visit Marker
    if (input.icon === "dot") {
      if (input.visitEntries && input.visitEntries.length > 1) {
        // Multi-visit patient marker
        providerMarker.innerHTML = generateMultiVisitPatientMarker(input.visitEntries, input.colorIndex);
        providerMarker.className = getMultiPatientMarkerClass(input.visitEntries);

        const patientEntries = providerMarker.querySelectorAll('.visit-entry-hover');
        patientEntries.forEach((entry, entryIndex) => {
          entry.addEventListener("mouseover", (event: Event) => {
            const visit = input.visitEntries![entryIndex];
            const tooltipText = visit.fullLabel
              ? visit.fullLabel
              : `${visit.timestamp.toLocaleString()}\n(${visit.providerName || 'Unknown'})`;
            showCustomTooltip(event as MouseEvent, tooltipText);
          });
          
          entry.addEventListener("mouseout", hideCustomTooltip);
          
          entry.addEventListener("click", (event: Event) => {
            event.stopPropagation();
            hideCustomTooltip();
            
            const visit = input.visitEntries![entryIndex];
            if (visit.patientId) {
              onPatientClick(visit.patientId);
            }
          });
        });
        
      } else {
        // Single-visit patient marker
        applySinglePatientMarkerStyling(providerMarker, input.colorIndex);
        providerMarker.innerText = generateSinglePatientMarker(input.patient);
        
        providerMarker.addEventListener("mouseover", (event) => {
          showCustomTooltip(event, input.label);
        });
      }
    } 
    // Provider Visit Marker
    else {
      if (input.visitEntries && input.visitEntries.length > 1) {
        providerMarker.innerHTML = generateMultiVisitProviderMarker(input.visitEntries, iconLookup.get(input.icon) || '');
        providerMarker.className = getMultiProviderMarkerClass(input.visitEntries);
        
        const visitEntries = providerMarker.querySelectorAll('.visit-entry-hover');
        visitEntries.forEach((entry, entryIndex) => {
          entry.addEventListener("mouseover", (event: Event) => {
            const visit = input.visitEntries![entryIndex];
            showCustomTooltip(event as MouseEvent, visit.timestamp.toLocaleString());
          });
          
          entry.addEventListener("mouseout", hideCustomTooltip);
          
          entry.addEventListener("click", (event: Event) => {
            event.stopPropagation();
            hideCustomTooltip();
            
            const visit = input.visitEntries![entryIndex];
            
            if (visit.patientId) {
              onPatientClick(visit.patientId);
            }
          });
        });
        
      } else {
        providerMarker.innerHTML = generateSingleProviderMarker(iconLookup.get(input.icon) || '', input.label);
        providerMarker.className = getSingleProviderMarkerClass(input.highlighted);

        providerMarker.addEventListener("mouseover", (event) => {
          showCustomTooltip(event, input.timestamp.toLocaleString());
        });
      }
    }

    // Always hide the tooltip when the mouse leaves the marker
    providerMarker.addEventListener("mouseout", hideCustomTooltip);

    // Create the marker on the map
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: input.point,
      content: providerMarker,
      zIndex: BASE_Z_INDEX + index,
    });

    const bringToFront = () => {
      allMarkers.forEach((m, i) => {
        if (m !== marker) {
          m.zIndex = BASE_Z_INDEX + i;
        }
      });
      marker.zIndex = HOVER_Z_INDEX;
    };

    const resetZIndex = () => {
      marker.zIndex = BASE_Z_INDEX + index;
    };

    providerMarker.addEventListener("mouseenter", bringToFront);
    providerMarker.addEventListener("mouseleave", resetZIndex);

    allMarkers.push(marker);

    if (input.patient?.id) {
      marker.addListener("click", () => {
        hideCustomTooltip()
        if (input.patient) {
          onPatientClick(input.patient.id)
        }
      });
    }

    return marker;
  });

  if (displayRoute) {
    const directionsService = new google.maps.DirectionsService();
    const directionsRenderer = new google.maps.DirectionsRenderer();
    directionsRenderer.setMap(map);
    calculateAndDisplayRoute(
      directionsService,
      directionsRenderer,
      getPointsOnly(mapMarkerInputs)
    );
  }

  // group overlapping markers together
  if (clusterMarkers) {
    new MarkerClusterer({ 
      markers, 
      map,
      algorithmOptions: {
        maxZoom: 15
      }
    });
  }
};

const mapBoundaries = (
  map: google.maps.Map,
  mapMarkerInputs: MapMarkerInput[],
  trackingPoints: MapPoint[],
  targetLocation?: MapPoint,
) => {
  const allPoints = trackingPoints.concat(getPointsOnly(mapMarkerInputs));
  if (targetLocation) allPoints.push(targetLocation);

  // Set map boundaries
  if (!allPoints) return;
  if (verbose) console.log("Set boundaries", allPoints);

  const bounds = new google.maps.LatLngBounds();
  allPoints.forEach((point) => {
    bounds.extend(point);
  });

  // center and zoom the map
  map.setCenter(bounds.getCenter());
  map.fitBounds(bounds, 50);

  // zoom out to at least city level
  const zoom = map.getZoom();
  if (zoom) map.setZoom(Math.min(11, zoom));
};

type MapAnnotationProps = Pick<
  MapWrapperProps,
  "trackingPoints" | "trackingMarker" | "markerInputs" | "displayRoute" | "clusterMarkers" | "targetLocation" | "onPatientClick"
>;

const MapAnnotations: FunctionComponent<MapAnnotationProps> = ({
  trackingPoints,
  trackingMarker,
  markerInputs,
  targetLocation,
  displayRoute,
  clusterMarkers,
  onPatientClick
}) => {
  const map = useMap(); // why does this not like mapId?
  const markerLibrary = useMapsLibrary("marker");
  const apiIsLoaded = useApiIsLoaded();
  const [targetMarker, setTargetMarker] = useState<google.maps.marker.AdvancedMarkerElement>();

  useEffect(() => {
    if(apiIsLoaded && !targetMarker) {
      // A terrible hack to try to make sure that the maps api is fully loaded
      // "apiIsLoaded" only gets you half way there for some reason.
      setTimeout(() => setTargetMarker(new google.maps.marker.AdvancedMarkerElement({zIndex: 1000})), 500);
    }
  }, [apiIsLoaded]);

  useEffect(() => {
    if(!targetLocation && targetMarker) {
      targetMarker.map = undefined;
      targetMarker.position = undefined;
    }

    if (
      !map ||
      !markerLibrary ||
      (markerInputs.length == 0 && trackingPoints.length == 0 && !targetLocation)
    )
      return;
    if (verbose) console.log("Annotate map", markerInputs, trackingPoints, targetLocation);
    mapBoundaries(map, markerInputs, trackingPoints, targetLocation);
    TrackingComponent(map, trackingPoints, trackingMarker);
    MarkerComponent(map, markerInputs, displayRoute, clusterMarkers, onPatientClick);
    
    if (targetLocation && targetMarker) {
      targetMarker.position = targetLocation;
      // Another terrible hack. Without this delay markers
      // aren't rendered reliably on the route pop-up on command center
      setTimeout(() => {
        targetMarker.map = map;
      }, 250)
    }
  }, [map, markerLibrary, markerInputs, trackingPoints, trackingMarker, targetLocation, targetMarker]);

  return <></>;
};


const INITIAL_CAMERA = {
  center: {lat: 0, lng: 0},
  zoom: 10
};

export default function GoogleMapWrapper({
  width,
  height,
  mapsApiKey,
  trackingPoints,
  trackingMarker,
  markerInputs,
  targetLocation,
  displayRoute,
  clusterMarkers,
  onPatientClick,
}: MapWrapperProps) {
  const allData = [width, height, JSON.stringify(trackingPoints), JSON.stringify(markerInputs), displayRoute].join('|')
  const uniqueMapId = `medarrive_map_${hashCode(allData).toString()}`;

  const [camera, setCamera] = useState(INITIAL_CAMERA);

  if (verbose) console.log(`Unique map id: ${uniqueMapId}`);
  if (markerInputs.length == 0 && trackingPoints.length == 0 && targetLocation == undefined) {
    return (<p>No locations to show</p>)
  }
  return (
    <APIProvider apiKey={mapsApiKey}>
      <GoogleMap
        style={{ width, height, minWidth: 300, minHeight: 300 }}
        center={camera.center}
        zoom={camera.zoom}
        gestureHandling={"greedy"}
        disableDefaultUI={true}
        zoomControl={true}
        mapId={uniqueMapId}
        reuseMaps={true}
        onCameraChanged={(e) => setCamera(e.detail)}
      />
      <MapAnnotations
        trackingPoints={trackingPoints}
        trackingMarker={trackingMarker}
        markerInputs={markerInputs}
        targetLocation={targetLocation}
        displayRoute={displayRoute}
        clusterMarkers={clusterMarkers}
        onPatientClick={onPatientClick}
      />
    </APIProvider>
  );
}
